import {
  ExceptionOutlined,
  FileOutlined,
  HeartOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  StarFilled,
  TwitterOutlined,
  WechatOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Space,
  Tag,
} from "antd";
import Emoji from "react-emoji-render";
import discord_logo from "apps/dots-frontend/public/discord_main.svg";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSupaClient } from "../../lib/supabase";
import { usePaywallStateDispatch } from "../Paywall/PaywallContext";
import Paywall from "../Paywall/Paywall";
import "./DotsHeader.css";
import useStripeCustomers from "../Settings/useStripeCustomers";

const { TextArea } = Input;

// TODO - refactor the analytics, make a general function for tracking and put this interface there. Follow tutorial (https://ashokraju.medium.com/using-segment-io-analytics-js-with-single-page-react-typescript-app-a8c12b4816c4)
declare global {
  interface Window {
    analytics: any;
  }
}

/**
 * Component to render Header and Help button with paths to Documentation, url, Feedback modal, Chat (TBD), Discord channel URL
 * return component to render
 */
const DotsHeader = () => {
  const supaClient = getSupaClient();
  const platform = localStorage.getItem("platform");
  const navigate = useNavigate();
  const guildId = localStorage.getItem("guild-id");
  const { billingCustomer, isLoading } = useStripeCustomers(guildId);
  const [loadingBilling, setLoadingBilling] = useState<boolean>(false);

  const [isFeedbackModalVisible, setFeedbackModalVisible] = useState(false);

  const [feedbackText, setFeedbackText] = useState("");
  // const [isChatSkillModalVisible, setIsChatSkillModalVisible] = useState(false); //To Be Added
  const [titleHeader, setTitleHeader] = useState({
    emoji: ":wave:",
    title: <span className="ml-2">Welcome!</span>,
    helpButton: true,
    showTitle: true,
  });
  const { loadingPlanTier, userPlanTier, trialing } = usePaywallStateDispatch();
  const { pathname } = useLocation();

  useEffect(() => {
    switch (pathname) {
      case "/home":
        setTitleHeader({
          emoji: ":wave:",
          title: <span className="ml-2">Welcome!</span>,
          helpButton: true,
          showTitle: true,
        });
        break;
      case "/messages":
        setTitleHeader({
          emoji: ":speech_balloon:",
          title: <span className="ml-2">Messages</span>,
          helpButton: true,
          showTitle: true,
        });
        break;
      case "/automation":
        setTitleHeader({
          emoji: "🤖",
          title: (
            <span className="ml-2">
              {platform === "slack" ? "Automation triggers" : "Bots"}
            </span>
          ),
          helpButton: true,
          showTitle: true,
        });
        break;
      case "/automation/new":
        setTitleHeader({
          emoji: "🤖",
          title: (
            <span className="ml-2">
              {platform === "slack" ? "Automation triggers" : "Bots"}
            </span>
          ),
          helpButton: true,
          showTitle: true,
        });
        break;
      case "/members":
        setTitleHeader({
          emoji: ":busts_in_silhouette:",
          title: <span className="ml-2">Members</span>,
          helpButton: true,
          showTitle: true,
        });
        break;
      case "/insights":
        setTitleHeader({
          emoji: ":chart_with_upwards_trend:",
          title: (
            <>
              <span className="ml-2">Your community insights</span>{" "}
              <Paywall
                free={null}
                pro={
                  trialing ? (
                    <span className="ml-3">
                      <Tag color="cyan" style={{ borderRadius: "99px" }}>
                        Available in Business tier
                      </Tag>
                    </span>
                  ) : null
                }
                business={null}
                tier={userPlanTier}
                loading={loadingPlanTier}
              />
            </>
          ),
          helpButton: true,
          showTitle: true,
        });
        break;
      case "/settings":
        setTitleHeader({
          emoji: ":gear:",
          title: <span className="ml-2">Settings</span>,
          helpButton: true,
          showTitle: true,
        });
        break;
      default:
        setTitleHeader({
          emoji: ":wave:",
          title: <span className="ml-2">Welcome!</span>,
          helpButton: true,
          showTitle: false,
        });
        break;
    }
  }, [pathname]);

  /**
   * Send feedback attained in the Feedback Modal to supabase table dots_user_feedback
   * @param @see FeedbackObj
   */
  interface FeedbackObj {
    created_at: Date;
    updated_at: Date;
    user_id: string;
    workspace: string;
    community: string;
    type: string;
    content: string;
  }
  const handleAddFeedback = async () => {
    const workspaceId =
      JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
        ?.currentSession.user?.app_metadata.workspace || "";
    const userId =
      JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
        ?.currentSession.user?.email || "";
    const currPlatform = localStorage.getItem("platform") || "";
    var communityID = { platform: "" };

    if (currPlatform === "slack") {
      communityID.platform = localStorage.getItem("team-id") || "";
    } else if (currPlatform === "discord") {
      communityID.platform = localStorage.getItem("guild-id") || "";
    }

    if (!feedbackText.length) {
      message.success("No Feedback written");
    } else {
      const feedbackObj: FeedbackObj = {
        created_at: new Date(),
        updated_at: new Date(),
        user_id: userId,
        workspace: workspaceId,
        community: communityID.platform,
        type: "feedback",
        content: feedbackText,
      };
      const { data, error } = await supaClient
        .from("dots_user_feedback")
        .insert([feedbackObj]);

      if (error) {
        message.error(error.message);
        window.analytics.track(
          "Error submitting feedback from help button modal:",
          {
            error: error,
          }
        );
      } else {
        message.success("Feedback submitted successfully! Thank you");
        setFeedbackText("");
        setFeedbackModalVisible(false);
      }
    }
  };

  // portal url to manage billing
  async function loadStripePortalUrl(customerId: string) {
    setLoadingBilling(true);
    const supabase = getSupaClient();
    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/billing/portal`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ customerId: customerId }),
      }
    );

    if (response.ok) {
      const responseJson = await response.json();

      window.location = responseJson.sessionUrl;
      setLoadingBilling(false);
    } else {
      console.log("Error getting a portal URL");
      setLoadingBilling(false);
      navigate("/settings");
    }
  }

  const resetInputField = () => {
    setFeedbackText("");
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" icon={<FileOutlined />}>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://faq.dots.community"
        >
          Help Docs
        </a>
      </Menu.Item>{" "}
      <Menu.Item
        key="4"
        icon={
          <img
            style={{ maxWidth: "12px", maxHeight: "12px" }}
            src={discord_logo}
          />
        }
      >
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://discord.gg/WJFTPtvGGw"
        >
          Join our Discord
        </a>
      </Menu.Item>{" "}
      <Menu.Item
        key="2"
        icon={<HeartOutlined />}
        onClick={() => {
          setFeedbackModalVisible(true);
        }}
      >
        {" "}
        Feedback
      </Menu.Item>
      <Menu.Item key="6" icon={<TwitterOutlined />}>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://twitter.com/dots_hq"
        >
          Follow us on twitter
        </a>
      </Menu.Item>{" "}
    </Menu>
  );

  const menuUpgrade = (
    <Menu>
      <div
        className="py-4 px-6 cursor-pointer"
        onClick={() => {
          if (!isLoading && billingCustomer) {
            loadStripePortalUrl(billingCustomer?.stripe_id);
          } else {
            navigate("/settings");
          }
        }}
      >
        <p className="text-dots-vibrant-orange text-base mb-4 text-center">
          Click here to upgrade your plan
        </p>
        {billingCustomer?.tier === "pro" ? (
          // Only show the Business plan if the user is on the Pro plan
          <table className="table-upgrade text-center">
            <tr>
              <th className="text-2xl">Business</th>
            </tr>
            <tr>
              <td className="pt-2 pl-4">
                <ul>
                  <li>Everything in Pro</li>
                  <li>Insights page</li>
                  <li>Unlimited exports</li>
                  <li>Enterprise integrations</li>
                  <li>Priority support</li>
                </ul>
              </td>
            </tr>
          </table>
        ) : (
          <table className="table-upgrade">
            <tr>
              <th className="text-left text-2xl">Pro</th>
              <th className="text-left text-2xl">Business</th>
            </tr>
            <tr>
              <td className="pt-4 pr-2 pl-4">
                <ul>
                  <li>Zapier integration</li>
                  <li>No Branding message</li>
                  <li>One month export limit</li>
                  <li>Drop off rate table on response tracking page</li>
                </ul>
              </td>
              <td className="pt-2 pl-4">
                <ul>
                  <li>Everything in Pro</li>
                  <li>Insights page</li>
                  <li>Unlimited exports</li>
                  <li>Enterprise integrations</li>
                  <li>Priority support</li>
                </ul>
              </td>
            </tr>
          </table>
        )}
      </div>
    </Menu>
  );
  return (
    <div className="mb-3">
      <Row>
        {titleHeader.showTitle && (
          <Col span={8}>
            <p
              className="whitespace-pre page-heading"
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Emoji text={titleHeader.emoji} />
              {titleHeader.title}
            </p>
          </Col>
        )}

        <Col
          span={titleHeader.showTitle ? 8 : 16}
          offset={8}
          style={{ textAlign: "end" }}
        >
          <Space>
            {platform === "discord" &&
              !["custom", "business"].includes(
                billingCustomer ? billingCustomer.tier : ""
              ) && (
                <Dropdown overlay={menuUpgrade}>
                  <Button
                    icon={<StarFilled />}
                    onClick={() => {
                      if (!isLoading && billingCustomer) {
                        loadStripePortalUrl(billingCustomer?.stripe_id);
                      } else {
                        navigate("/settings");
                      }
                    }}
                    type="primary"
                    loading={loadingBilling}
                  >
                    Upgrade{" "}
                  </Button>
                </Dropdown>
              )}
            {titleHeader.helpButton == true ? (
              <Dropdown
                className="helper-dropdown"
                overlay={menu}
                trigger={["click"]}
                placement="bottomRight"
                arrow
              >
                <Button icon={<QuestionCircleOutlined />}> Help </Button>
              </Dropdown>
            ) : null}
          </Space>
        </Col>
      </Row>
      <Space direction="vertical" size="middle" />
      <div style={{ position: "relative" }}>
        <Modal
          mask={false}
          style={{
            position: "absolute",
            top: "6em",
            right: "2em",
            width: "40em",
          }}
          onCancel={() => {
            setFeedbackModalVisible(false);
            resetInputField();
          }}
          visible={isFeedbackModalVisible}
          footer={null}
        >
          <div className="space-y-2">
            <div>Feedback</div>
            <TextArea
              style={{
                height: 90,
              }}
              value={feedbackText}
              placeholder="Please type your feedback here!"
              onChange={(e) => setFeedbackText(e.target.value)}
            />
            <Row style={{ height: "1vh" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                onClick={() => {
                  setFeedbackModalVisible(false);
                  resetInputField();
                }}
              >
                Cancel
              </Button>{" "}
              <Button type="primary" onClick={() => handleAddFeedback()}>
                Submit Feedback
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};
export default DotsHeader;
